import { clsx } from "clsx";
import { debounce } from "lodash-es";
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import type { FormatOptionLabelMeta } from "react-select";
import Select from "react-select";
import { AnimatedTooltip, HStack, Spacer, Tag, TagIntent, TooltipIntent } from "widgets";
import { useTooltip } from "widgets/hooks";

import { type Contig, type ContigSearch } from "../../graphql.js";
import { shouldLoadContigsRelatedToAGenome } from "../../redux/modules/contig-search.js";
import { type IAppState } from "../../redux/reducer.js";
import { type ReduxDispatch } from "../../redux/redux-utils.js";

export interface IContigSelectorProps {
  genomeId?: number;
  isVirtualGenome: boolean; // if a virtual genome, this triggers the display of the genome name in the options
  selectedContig: Contig;
  onContigChange: (contig: Contig) => void;
  classNames?: string;
}

const ContigSelector: FC<IContigSelectorProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();

  const [options, setOptions] = useState<Contig[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const isLoading = useSelector<IAppState, boolean>((state) => state.contigSearch.isLoading, shallowEqual);

  const triggerRef = useRef<HTMLDivElement>(null);
  const { triggerProps, tooltipProps, state } = useTooltip(triggerRef, {});

  //const debouncedSetSearchTerm = useMemo(() => debounce(setSearchTerm, 300, { onlyResolvesLast: true }), [setSearchTerm]); // previous debouncing
  const debouncedSetSearchTerm = useMemo(() => debounce(setSearchTerm, 300), [setSearchTerm]);

  useEffect(() => {
    if (props.genomeId) {
      dispatch(shouldLoadContigsRelatedToAGenome(props.genomeId, searchTerm)).then((contigSearch: ContigSearch) => {
        setOptions(contigSearch.contigs);
        if (!searchTerm && !props.selectedContig) {
          props.onContigChange(contigSearch.contigs[0]); // default selection
        }
      });
    } else {
      setOptions([]);
    }
  }, [props.genomeId, searchTerm]);

  const selectClassNames = clsx("react-select full-width", props.classNames);

  const formatContigOption = useCallback(
    (contig: Contig, meta?: FormatOptionLabelMeta<Contig>) => {
      if (!contig) {
        return null;
      }

      // we show the sequence type only in the menu, because takes too much place
      const formatSelectedValue = meta?.context === "value";

      const labelElements = [
        <span key={`${contig.id}-1`} style={{ whiteSpace: "nowrap" /*overflow: "hidden", textOverflow: "ellipsis"*/ }}>
          {`${contig.sequenceId} ${contig.name ? ` (${contig.name.trim()})` : ""}`}
        </span>,
      ];

      if (props.isVirtualGenome) {
        labelElements.push(
          <span key={`${contig.id}-2`} style={{ whiteSpace: "nowrap" }}>
            <strong>{contig.organism.name}</strong>
          </span>,
        );
      }

      // if we render the options for the option list (but neither the selected value nor the tooltip) we show the sequence type tag
      if (meta && !formatSelectedValue && contig.sequenceType) {
        labelElements.push(
          <>
            <Spacer />
            <Tag intentColor={TagIntent.Secondary}>{contig.sequenceType}</Tag>
          </>,
        );
      }

      return <HStack>{labelElements}</HStack>;
    },
    [props.isVirtualGenome],
  );

  return (
    <>
      <div {...triggerProps} ref={triggerRef}>
        <Select
          key={`contig-selector`}
          className={selectClassNames}
          classNamePrefix={"react-select"}
          name="contigSelection"
          value={props.selectedContig}
          isClearable={false}
          isSearchable={true}
          options={options}
          getOptionValue={(option: Contig) => option.id}
          getOptionLabel={(option: Contig) =>
            option && `${option.sequenceId} ${option.name} ${option.sequenceType} ${option.organism.name}`
          } // for filtering
          formatOptionLabel={formatContigOption}
          onChange={props.onContigChange}
          onInputChange={debouncedSetSearchTerm}
          menuPlacement={"auto"}
          menuPosition={"fixed"} // required if in a popover e.g. in the Save Flag dialog
          menuPortalTarget={document.body}
          isDisabled={!props.genomeId}
          isLoading={isLoading}
          styles={{
            menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
            menu: (styles) => ({ ...styles, width: "max-content", minWidth: "100%" }),
          }}
        />
      </div>
      <AnimatedTooltip
        // AnimatedTooltip has a maxWidth set which is not suitable here
        style={{ maxWidth: "unset" }}
        targetRef={triggerRef}
        state={state}
        domProps={tooltipProps}
        intent={TooltipIntent.Default}
      >
        {formatContigOption(props.selectedContig)}
      </AnimatedTooltip>
    </>
  );
};

export { ContigSelector };
